import React, { useEffect, useState, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import FormatBar from '../format_bar/formatBar';
import './adminShop.css';

// Hjælpefunktion til at normalisere whitespace i HTML-indhold
const normalizeWhitespace = (html) => {
  if (!html) return '';

  // Erstat alle newlines med space
  let normalized = html.replace(/\n/g, ' ');

  // Erstat multiple spaces med et enkelt space
  normalized = normalized.replace(/\s+/g, ' ');

  // Opdeler HTML i dele baseret på tags
  const parts = [];
  let currentIndex = 0;
  const regex = /<[^>]+>/g;
  let match;

  while ((match = regex.exec(normalized)) !== null) {
    // Tekst før tag
    if (match.index > currentIndex) {
      const textBefore = normalized.substring(currentIndex, match.index);
      parts.push(textBefore);
    }

    // Tag'et selv
    parts.push(match[0]);
    currentIndex = match.index + match[0].length;
  }

  // Tekst efter sidste tag
  if (currentIndex < normalized.length) {
    parts.push(normalized.substring(currentIndex));
  }

  // Genopbyg HTML, mens vi sikrer korrekt whitespace
  return parts.map(part => {
    // Hvis det er et tag, returner det som det er
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    // Hvis det er tekst (ikke et tag), trim kun leading/trailing whitespace
    return part;
  }).join('');
};

const AdminShop = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingContent, setEditingContent] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  const [heroPreview, setHeroPreview] = useState('');
  const [productImage, setProductImage] = useState(null);
  const [productPreview, setProductPreview] = useState('');
  
  const introTextRef = useRef(null);
  const orderTextRef = useRef(null);
  const productDescriptionRef = useRef(null);

  const [shopData, setShopData] = useState({
    title: 'Shop',
    heroImage: '',
    introText: '',
    orderText: ''
  });
  
  const [produkter, setProdukter] = useState([]);
  
  // Format states
  const [introTextFormats, setIntroTextFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [orderTextFormats, setOrderTextFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [productDescriptionFormats, setProductDescriptionFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        navigate('/admin/login');
      } else {
        fetchShopContent();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchShopContent = async () => {
    try {
      setIsLoading(true);
      
      // Fetch main shop content
      const shopDocRef = doc(db, 'content', 'shop');
      const shopDoc = await getDoc(shopDocRef);
      
      if (shopDoc.exists()) {
        const data = shopDoc.data();
        setShopData({
          title: data.title || 'Shop',
          heroImage: data.heroImage || '',
          introText: data.introText || '',
          orderText: data.orderText || ''
        });
        
        if (data.heroImage) {
          setHeroPreview(data.heroImage);
        }
      }
      
      // Fetch shop products
      const produkterDocRef = doc(db, 'content', 'produkter');
      const produkterDoc = await getDoc(produkterDocRef);
      
      if (produkterDoc.exists() && produkterDoc.data().items) {
        setProdukter(produkterDoc.data().items);
      } else {
        setProdukter([]);
      }
    } catch (error) {
      console.error('Fejl ved hentning af shop data:', error);
      setMessage({
        text: 'Der opstod en fejl ved indlæsning af indhold',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initialisering af editorer
  useEffect(() => {
    if (editingContent) {
      if (introTextRef.current) {
        introTextRef.current.innerHTML = shopData.introText || '';
      }
      if (orderTextRef.current) {
        orderTextRef.current.innerHTML = shopData.orderText || '';
      }
    }

    if (editingProduct && productDescriptionRef.current) {
      productDescriptionRef.current.innerHTML = editingProduct.produktBeskrivelse || '';
    }
  }, [editingContent, editingProduct, shopData.introText, shopData.orderText]);

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isIntroText = introTextRef.current && introTextRef.current.contains(activeElement);
      const isOrderText = orderTextRef.current && orderTextRef.current.contains(activeElement);
      const isProductDescription = productDescriptionRef.current && productDescriptionRef.current.contains(activeElement);
      
      if (isIntroText) {
        checkActiveFormats('introText');
      } else if (isOrderText) {
        checkActiveFormats('orderText');
      } else if (isProductDescription) {
        checkActiveFormats('productDescription');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [editingContent, editingProduct]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      introText: introTextRef.current,
      orderText: orderTextRef.current,
      productDescription: productDescriptionRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      introText: setIntroTextFormats,
      orderText: setOrderTextFormats,
      productDescription: setProductDescriptionFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      introText: introTextRef.current,
      orderText: orderTextRef.current,
      productDescription: productDescriptionRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    // For rich text editors
    if (e.key === 'Enter' && e.target.contentEditable === 'true') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
      return;
    }
    
    // For textarea editors
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName === 'TEXTAREA') {
      e.preventDefault();
      
      // Indsæt et linjeskift på cursor-positionen
      const { name, selectionStart, selectionEnd, value } = e.target;
      const newValue = value.substring(0, selectionStart) + "\n" + value.substring(selectionEnd);
      
      // Opdater værdien baseret på hvilken komponent der redigeres
      if (editingContent) {
        setShopData(prev => ({
          ...prev,
          [name]: newValue
        }));
      } else if (editingProduct) {
        setEditingProduct(prev => ({
          ...prev,
          [name]: newValue
        }));
      }
      
      // Efter komponentopdatering, flyt cursoren til efter den nye linje
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
      }, 0);
    }
  };

  const handleSaveContent = async () => {
    try {
      setIsSaving(true);
      
      let updatedShopData = { ...shopData };
      
      // Upload hero image if it exists
      if (heroImage) {
        const heroStorageRef = ref(storage, `shop/hero-${Date.now()}`);
        await uploadBytes(heroStorageRef, heroImage);
        const heroImageUrl = await getDownloadURL(heroStorageRef);
        updatedShopData.heroImage = heroImageUrl;
      }
      
      // Hent det formaterede indhold fra editorerne og normaliser whitespace
      const introText = introTextRef.current ? normalizeWhitespace(introTextRef.current.innerHTML) : '';
      const orderText = orderTextRef.current ? normalizeWhitespace(orderTextRef.current.innerHTML) : '';
      
      // Save to Firestore
      await setDoc(doc(db, 'content', 'shop'), {
        ...updatedShopData,
        introText,
        orderText,
        lastUpdated: new Date()
      });
      
      // Update local state with new data
      setShopData(prev => ({
        ...prev,
        introText,
        orderText,
        heroImage: updatedShopData.heroImage
      }));
      
      if (updatedShopData.heroImage) {
        setHeroPreview(updatedShopData.heroImage);
      }
      
      setMessage({
        text: 'Indholdet er blevet gemt',
        type: 'success'
      });
      
      // Reset state
      setHeroImage(null);
      setEditingContent(false);
    } catch (error) {
      console.error('Fejl ved lagring af indhold:', error);
      setMessage({
        text: 'Der opstod en fejl ved gemning af indhold',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShopData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHeroImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroImage(file);
      
      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProductImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setProductImage(file);
      
      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateProduct = () => {
    setEditingProduct({
      id: '',
      navn: '',
      pris: '',
      kort_beskrivelse: '',
      billede: '',
      produktBeskrivelse: '',
      beskrivelse: [''],
      features: [''],
      anvendelse: ['']
    });
    setProductPreview('');
    setProductImage(null);
  };

  const handleEditProduct = (index) => {
    setEditingProduct({...produkter[index], index});
    setProductPreview(produkter[index].billede);
    setProductImage(null);
  };

  const handleDeleteProduct = async (index) => {
    if (window.confirm('Er du sikker på, at du vil slette dette produkt?')) {
      try {
        setIsSaving(true);
        const updatedProdukter = [...produkter];
        
        // Try to delete the image from storage if it starts with the firebase storage URL
        const deletedProduct = updatedProdukter[index];
        if (deletedProduct.billede && deletedProduct.billede.includes('firebasestorage.googleapis.com')) {
          try {
            const imageRef = ref(storage, deletedProduct.billede);
            await deleteObject(imageRef);
          } catch (error) {
            console.error('Fejl ved sletning af billede:', error);
            // Continue with deletion even if image deletion fails
          }
        }
        
        // Remove the product from the array
        updatedProdukter.splice(index, 1);
        
        // Update Firestore
        await setDoc(doc(db, 'content', 'produkter'), { items: updatedProdukter });
        
        // Update local state
        setProdukter(updatedProdukter);
        
        setMessage({
          text: 'Produktet er blevet slettet',
          type: 'success'
        });
      } catch (error) {
        console.error('Fejl ved sletning af produkt:', error);
        setMessage({
          text: 'Der opstod en fejl ved sletning af produktet',
          type: 'error'
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleProductInputChange = (e) => {
    const { name, value } = e.target;
    setEditingProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayInputChange = (arrayName, index, value) => {
    setEditingProduct(prev => {
      const newArray = [...prev[arrayName]];
      newArray[index] = value;
      return {
        ...prev,
        [arrayName]: newArray
      };
    });
  };

  const handleAddArrayItem = (arrayName) => {
    setEditingProduct(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], '']
    }));
  };

  const handleRemoveArrayItem = (arrayName, index) => {
    setEditingProduct(prev => {
      const newArray = [...prev[arrayName]];
      newArray.splice(index, 1);
      return {
        ...prev,
        [arrayName]: newArray
      };
    });
  };

  const formatProductDescription = (product) => {
    if (!product) return '';
    
    let formatted = '';
    
    // Først beskrivelsesafsnitene
    if (product.beskrivelse && product.beskrivelse.length > 0) {
      formatted += product.beskrivelse.join('\n\n');
    }
    
    // Tilføj specifikationer med overskrift
    if (product.features && product.features.length > 0) {
      formatted += '\n\n## Specifikationer\n';
      formatted += product.features.map(feature => `- ${feature}`).join('\n');
    }
    
    // Tilføj anvendelsesmuligheder med overskrift
    if (product.anvendelse && product.anvendelse.length > 0) {
      formatted += '\n\n## Anvendelse\n';
      formatted += product.anvendelse.map(anv => `- ${anv}`).join('\n');
    }
    
    return formatted;
  };

  const handleSaveProduct = async () => {
    try {
      if (!editingProduct.navn || !editingProduct.pris || !editingProduct.kort_beskrivelse) {
        setMessage({
          text: 'Udfyld venligst alle påkrævede felter',
          type: 'error'
        });
        return;
      }
      
      setIsSaving(true);
      
      let updatedProduct = { ...editingProduct };
      
      // Generate id from name if not exists
      if (!updatedProduct.id) {
        updatedProduct.id = updatedProduct.navn
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/æ/g, 'ae')
          .replace(/ø/g, 'o')
          .replace(/å/g, 'a')
          .replace(/[^a-z0-9-]/g, '');
      }
      
      // Set slug
      updatedProduct.slug = `/shop/${updatedProduct.id}`;
      
      // Upload product image if it exists
      if (productImage) {
        const productStorageRef = ref(storage, `shop/products/${updatedProduct.id}-${Date.now()}`);
        await uploadBytes(productStorageRef, productImage);
        const productImageUrl = await getDownloadURL(productStorageRef);
        updatedProduct.billede = productImageUrl;
      }

      // Hent det formaterede indhold fra editoren og normaliser whitespace
      const produktBeskrivelse = productDescriptionRef.current ? normalizeWhitespace(productDescriptionRef.current.innerHTML) : '';
      updatedProduct.produktBeskrivelse = produktBeskrivelse;
      
      // Sørg for at produkt-arrays altid eksisterer (for kompatibilitet)
      updatedProduct.beskrivelse = updatedProduct.beskrivelse || [''];
      updatedProduct.features = updatedProduct.features || [''];
      updatedProduct.anvendelse = updatedProduct.anvendelse || [''];
      
      let updatedProdukter;
      
      if (typeof updatedProduct.index === 'number') {
        // Update existing product
        updatedProdukter = [...produkter];
        updatedProdukter[updatedProduct.index] = updatedProduct;
      } else {
        // Add new product
        updatedProdukter = [...produkter, updatedProduct];
      }
      
      // Remove the index property
      updatedProdukter = updatedProdukter.map(({ index, ...rest }) => rest);
      
      // Save to Firestore
      await setDoc(doc(db, 'content', 'produkter'), { items: updatedProdukter });
      
      // Update local state
      setProdukter(updatedProdukter);
      
      setMessage({
        text: 'Produktet er blevet gemt',
        type: 'success'
      });
      
      // Reset state
      setEditingProduct(null);
      setProductImage(null);
      setProductPreview('');
    } catch (error) {
      console.error('Fejl ved gemning af produkt:', error);
      setMessage({
        text: 'Der opstod en fejl ved gemning af produktet',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (editingContent) {
      setEditingContent(false);
    } else if (editingProduct) {
      setEditingProduct(null);
    }
    setHeroImage(null);
    setProductImage(null);
    setProductPreview('');
  };

  const clearMessage = () => {
    setMessage({ text: '', type: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={goBack} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-shop-container">
        <button className="back-button" onClick={goBack}>
          ← Tilbage til Admin
        </button>
        
        <h1>Administrer Shop</h1>
        
        {message.text && (
          <div className={`admin-message ${message.type}`}>
            <p>{message.text}</p>
            <button 
              className="close-message" 
              onClick={clearMessage}
            >
              ×
            </button>
          </div>
        )}
        
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Hovedindhold</h2>
            {!editingContent && (
              <button 
                className="edit-content-btn"
                onClick={() => setEditingContent(true)}
              >
                Rediger indhold
              </button>
            )}
          </div>
          
          {editingContent ? (
            <div className="shop-form">
              <div className="form-group">
                <label htmlFor="title">Overskrift</label>
                <input 
                  type="text" 
                  id="title" 
                  name="title"
                  value={shopData.title}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="heroImage">Hero billede</label>
                <div className="image-uploader">
                  <input 
                    type="file" 
                    id="heroImage" 
                    accept="image/*"
                    onChange={handleHeroImageChange}
                  />
                  <button 
                    type="button" 
                    className="upload-btn"
                    onClick={() => document.getElementById('heroImage').click()}
                  >
                    Vælg billede
                  </button>
                </div>
                
                {heroPreview && (
                  <div className="hero-preview">
                    <img src={heroPreview} alt="Hero preview" />
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="introText">Introduktionstekst</label>
                <FormatBar 
                  activeFormats={introTextFormats}
                  onFormatClick={(format) => handleFormatClick(format, 'introText')}
                />
                <div
                  ref={introTextRef}
                  className="rich-text-editor"
                  contentEditable
                  onKeyDown={handleKeyDown}
                  onFocus={() => handleEditorFocus('introText')}
                  data-placeholder="Skriv introduktionstekst til shoppen..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="orderText">Bestillingstekst</label>
                <FormatBar 
                  activeFormats={orderTextFormats}
                  onFormatClick={(format) => handleFormatClick(format, 'orderText')}
                />
                <div
                  ref={orderTextRef}
                  className="rich-text-editor"
                  contentEditable
                  onKeyDown={handleKeyDown}
                  onFocus={() => handleEditorFocus('orderText')}
                  data-placeholder="Skriv information om bestillingsprocessen..."
                />
              </div>
              
              <div className="form-actions">
                <button 
                  className="save-btn"
                  onClick={handleSaveContent}
                  disabled={isSaving}
                >
                  {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                </button>
                <button 
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Annuller
                </button>
              </div>
            </div>
          ) : (
            <div className="preview-section">
              <p className="admin-description">
                Her kan du redigere hovedindholdet på shopsiden, inklusiv overskrift, billede og tekster.
              </p>
              
              <h3>Forhåndsvisning</h3>
              
              <div className="preview-hero-image">
                {shopData.heroImage ? (
                  <img src={shopData.heroImage} alt="Shop hero" />
                ) : (
                  <div className="no-image">Intet billede valgt</div>
                )}
              </div>
              
              <h3>Introduktionstekst</h3>
              <div dangerouslySetInnerHTML={{ __html: shopData.introText || 'Ingen introduktionstekst' }} />
              
              <h3>Bestillingstekst</h3>
              <div dangerouslySetInnerHTML={{ __html: shopData.orderText || 'Ingen bestillingstekst' }} />
            </div>
          )}
        </div>
        
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Produkter</h2>
            {!editingProduct && (
              <button 
                className="create-btn"
                onClick={handleCreateProduct}
              >
                + Opret nyt produkt
              </button>
            )}
          </div>
          
          {editingProduct ? (
            <div className="shop-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="produkt-navn">Navn</label>
                  <input 
                    type="text" 
                    id="produkt-navn" 
                    name="navn"
                    value={editingProduct.navn}
                    onChange={handleProductInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="produkt-pris">Pris</label>
                  <input 
                    type="text" 
                    id="produkt-pris" 
                    name="pris"
                    value={editingProduct.pris}
                    onChange={handleProductInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="produkt-kort_beskrivelse">Kort beskrivelse</label>
                <input 
                  type="text" 
                  id="produkt-kort_beskrivelse" 
                  name="kort_beskrivelse"
                  value={editingProduct.kort_beskrivelse}
                  onChange={handleProductInputChange}
                  required
                />
                <span className="form-help-text">
                  Kort beskrivelse der vises på produktkortet i oversigten
                </span>
              </div>
              
              <div className="form-group">
                <label htmlFor="produkt-billede">Produktbillede</label>
                <div className="image-uploader">
                  <input 
                    type="file" 
                    id="produkt-billede" 
                    accept="image/*"
                    onChange={handleProductImageChange}
                  />
                  <button 
                    type="button" 
                    className="upload-btn"
                    onClick={() => document.getElementById('produkt-billede').click()}
                  >
                    Vælg billede
                  </button>
                </div>
                
                {productPreview && (
                  <div className="image-preview">
                    <img src={productPreview} alt="Produkt preview" />
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="produktBeskrivelse">Produktbeskrivelse</label>
                <FormatBar 
                  activeFormats={productDescriptionFormats}
                  onFormatClick={(format) => handleFormatClick(format, 'productDescription')}
                />
                <div
                  ref={productDescriptionRef}
                  className="rich-text-editor"
                  contentEditable
                  onKeyDown={handleKeyDown}
                  onFocus={() => handleEditorFocus('productDescription')}
                  data-placeholder="Skriv en detaljeret beskrivelse af produktet..."
                />
                <span className="form-help-text">
                  Du kan formatere teksten ved at bruge formateringsværktøjerne ovenfor.
                </span>
              </div>
              
              <div className="form-actions">
                <button 
                  className="save-btn"
                  onClick={handleSaveProduct}
                  disabled={isSaving}
                >
                  {isSaving ? 'Gemmer...' : 'Gem produkt'}
                </button>
                <button 
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Annuller
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="admin-description">
                Her kan du administrere produkter i shoppen. Du kan oprette, redigere og slette produkter.
              </p>
              
              {produkter.length > 0 ? (
                <div className="shop-list">
                  {produkter.map((produkt, index) => (
                    <div key={produkt.id} className="shop-item">
                      <div className="shop-preview">
                        <div className="shop-image">
                          {produkt.billede ? (
                            <img src={produkt.billede} alt={produkt.navn} />
                          ) : (
                            <div className="no-image">Intet billede</div>
                          )}
                        </div>
                        <div className="shop-content">
                          <h3>{produkt.navn}</h3>
                          <p className="shop-price">{produkt.pris}</p>
                          <p>{produkt.kort_beskrivelse}</p>
                        </div>
                      </div>
                      <div className="shop-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditProduct(index)}
                        >
                          Rediger
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteProduct(index)}
                        >
                          Slet
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-items">
                  <p>Der er endnu ingen produkter i shoppen.</p>
                  <p>Klik på "Opret nyt produkt" for at tilføje det første produkt.</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminShop;
